import React, { useEffect, useState, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { assets } from "@/lib/media";
import { ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

// Curated slideshow — swap/extend freely as new real photos come in.
// `positionMobile` overrides `position` on narrow viewports where object-cover
// would otherwise crop the subject out of frame.
const SLIDES = [
  { src: assets.weddingKiss,        caption: "Casamentos",       position: "center 35%", positionMobile: "center 35%" },
  { src: assets.christmasBlueDress, caption: "Ensaio de Natal",  position: "center 30%", positionMobile: "center 30%" },
  { src: assets.contactBooking,     caption: "Celebrações",      position: "center 30%", positionMobile: "center 30%" },
  { src: assets.familyLaughingGirl, caption: "Família",          position: "center 12%", positionMobile: "center 20%" },
  { src: assets.portraitFurHood,    caption: "Editorial & Moda", position: "center center", positionMobile: "78% center" },
];

const SLIDE_DURATION = 5500; // ms per slide
const FADE_DURATION = 1200;  // ms crossfade

export default function Hero() {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);
    onChange(mq);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange);
    };
  }, []);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);
  const goTo = useCallback((i) => setIndex(i), []);

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(next, SLIDE_DURATION);
    return () => clearInterval(id);
  }, [paused, next, index]);

  // Preload next slide for smoother transitions
  useEffect(() => {
    const n = (index + 1) % SLIDES.length;
    const img = new Image();
    img.src = SLIDES[n].src;
  }, [index]);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const titleLines = t("hero.title").split("\n");

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-[100svh] w-full overflow-hidden bg-ocean text-sand grain"
    >
      {/* Slides stack — crossfade with opacity + Ken Burns */}
      <div className="absolute inset-0">
        {SLIDES.map((slide, i) => {
          const active = i === index;
          return (
            <div
              key={slide.src}
              data-testid={`hero-slide-${i}`}
              className="absolute inset-0 will-change-[opacity,transform]"
              style={{
                opacity: active ? 1 : 0,
                transition: `opacity ${FADE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                zIndex: active ? 2 : 1,
              }}
              aria-hidden={!active}
            >
              <img
                src={slide.src}
                alt={slide.caption}
                className="absolute inset-0 h-full w-full object-cover"
                style={{
                  objectPosition: isMobile && slide.positionMobile ? slide.positionMobile : slide.position,
                  // Ken Burns: slow zoom while the slide is active
                  transform: active ? "scale(1.08)" : "scale(1.0)",
                  transition: `transform ${SLIDE_DURATION + FADE_DURATION}ms ease-out`,
                }}
                draggable={false}
              />
            </div>
          );
        })}
      </div>

      {/* Cinematic gradient overlays */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[3] bg-gradient-to-b from-ocean/70 via-ocean/30 to-ocean/80"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-[3] w-1/2 bg-gradient-to-r from-ocean/60 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] h-48 bg-gradient-to-t from-ocean to-transparent"
      />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 pt-32 sm:px-10 sm:pb-28">
        <div className="max-w-4xl animate-fade-up">
          <p
            data-testid="hero-overline"
            className="mb-6 text-[11px] uppercase tracking-[0.3em] text-terracotta/90"
          >
            {t("hero.overline")}
          </p>
          <h1
            data-testid="hero-title"
            className="font-display text-5xl leading-[1.02] text-sand sm:text-7xl lg:text-[5.5rem]"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="block">
                {i === 1 ? <em className="font-light italic">{line}</em> : line}
              </span>
            ))}
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-8 max-w-2xl text-base leading-relaxed text-sand/85 sm:text-lg"
          >
            {t("hero.subtitle")}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-3 rounded-sm border border-terracotta bg-terracotta px-8 py-4 text-[11px] uppercase tracking-[0.24em] text-sand transition-all duration-300 hover:-translate-y-0.5 hover:border-sand hover:bg-sand hover:text-ocean"
            >
              {t("hero.cta_primary")}
            </a>
            <a
              href="#portfolio"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 rounded-sm border border-sand/40 px-8 py-4 text-[11px] uppercase tracking-[0.24em] text-sand transition-all duration-300 hover:border-sand hover:bg-sand/10"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>
        </div>

        {/* Slideshow controls — bottom row, elegant and minimal */}
        <div className="pointer-events-none absolute inset-x-0 bottom-10 z-10 flex items-end justify-between px-6 sm:px-10">
          {/* Left: numeric counter + dots */}
          <div className="pointer-events-auto flex items-center gap-6">
            <span
              data-testid="hero-counter"
              className="font-display text-xl tracking-widest text-sand/90 tabular-nums"
            >
              <span className="text-terracotta">{String(index + 1).padStart(2, "0")}</span>
              <span className="mx-2 text-sand/40">/</span>
              <span>{String(SLIDES.length).padStart(2, "0")}</span>
            </span>
            <div className="hidden items-center gap-2 sm:flex">
              {SLIDES.map((_, i) => {
                const active = i === index;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Ir para slide ${i + 1}`}
                    data-testid={`hero-dot-${i}`}
                    className="group h-[2px] overflow-hidden bg-sand/25 transition-all"
                    style={{ width: active ? 44 : 18 }}
                  >
                    <span
                      className="block h-full origin-left bg-terracotta"
                      style={{
                        transform: active && !paused ? "scaleX(1)" : active ? "scaleX(0.35)" : "scaleX(0)",
                        transition: active && !paused
                          ? `transform ${SLIDE_DURATION}ms linear`
                          : "transform 250ms ease-out",
                      }}
                    />
                  </button>
                );
              })}
            </div>
            <span className="hidden text-[10px] uppercase tracking-[0.3em] text-sand/60 md:inline">
              {SLIDES[index].caption}
            </span>
          </div>

          {/* Right: arrows + scroll cue */}
          <div className="pointer-events-auto flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              aria-label="Slide anterior"
              data-testid="hero-prev"
              className="rounded-full border border-sand/30 bg-ocean/20 p-2 text-sand/80 backdrop-blur-md transition-all hover:border-sand hover:bg-sand hover:text-ocean"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.4} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Próximo slide"
              data-testid="hero-next"
              className="rounded-full border border-sand/30 bg-ocean/20 p-2 text-sand/80 backdrop-blur-md transition-all hover:border-sand hover:bg-sand hover:text-ocean"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.4} />
            </button>
            <div className="ml-4 hidden flex-col items-center gap-2 sm:flex">
              <span className="text-[10px] uppercase tracking-[0.3em] text-sand/60">
                {t("hero.scroll")}
              </span>
              <ArrowDown className="h-4 w-4 animate-bounce text-sand/70" strokeWidth={1.2} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
